<template>
  <div class="former-input">
    <input
      v-if="type === 'patternize'"
      v-mask="pattern"
      v-bind="options"
      type="tel"
      :value="value"
      @blur="change"
      @input="input"
      @keydown.enter="submit"
    >

    <input
      v-else-if="type === 'monetize'"
      v-money="money"
      v-bind="options"
      type="tel"
      :value="value"
      @blur="change"
      @input="input"
      @keydown.enter="submit"
    >

    <textarea
      v-else
      v-bind="options"
      :value="value"
      @blur="change"
      @input="input"
      @keydown.enter="submit"
    />
  </div>
</template>

<script>
  import monetize from '@sdk/helpers/formatters/monetize';

  export default {
    name: 'FormerInput',
    props: {
      field: {
        type: Object,
        default() {
          return {};
        },
      },
      customClasses: {
        type: Array,
        default() {
          return [];
        },
      },
      color: {
        type: String,
        default: '',
      },
      size: {
        type: String,
        default: '',
      },
      value: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      autofocus: {
        type: Boolean,
        default: false,
      },
      focusless: {
        type: Boolean,
        default: false,
      },
      center: {
        type: Boolean,
        default: false,
      },
      bold: {
        type: Boolean,
        default: false,
      },
      multiline: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        height: 0,
      };
    },
    computed: {
      classes() {
        const classes = [
          ...this.customClasses,
          'former-input-field',
        ];

        const {
          color,
          size,
          type,
          value,
          field,
          focusless,
          center,
          bold,
          disabled,
          multiline,
        } = this;

        if (type === 'monetize' && (!value || value === monetize(0))) {
          classes.push('former-input-field-empty');
        }

        if (disabled) {
          classes.push('former-input-field-disabled');
        }

        if (focusless) {
          classes.push('former-input-field-focusless');
        }

        if (center) {
          classes.push('former-input-field-center');
        }

        if (bold) {
          classes.push('former-input-field-bold');
        }

        if (field.errors && field.errors.length) {
          classes.push('former-input-field-error');
        }

        if (color) {
          classes.push(`former-input-field-color-${color}`);
        }

        if (size) {
          classes.push(`former-input-field-size-${size}`);
        }

        if (multiline) {
          classes.push('former-input-field-multiline');
        }

        return classes;
      },
      type() {
        switch (this.field.mask.type) {
          case 'patternize':
            return 'patternize';
          case 'monetize':
            return 'monetize';
          default:
            return 'text';
        }
      },
      pattern() {
        const {
          format,
          bigFormat,
        } = this.field.mask;

        if (bigFormat && this.value && this.value.length > format.length) {
          return bigFormat;
        }

        return format;
      },
      money() {
        const {
          prefix,
          suffix,
        } = this.field.mask;

        return {
          decimal: ',',
          prefix: prefix !== undefined ? prefix : 'R$ ',
          suffix,
          precision: 2,
          thousands: '.',
        };
      },
      options() {
        const {
          disabled,
          field,
        } = this;

        const {
          format,
          bigFormat,
        } = field.mask;

        const fieldFormat = bigFormat || format;

        return {
          class: this.classes,
          disabled: disabled || field.disabled,
          id: field.id,
          placeholder: field.placeholder,
          ref: 'input',
          rows: this.multiline ? 4 : undefined,
          maxlength: ![null, undefined].includes(fieldFormat) && fieldFormat.length,
        };
      },
    },
    watch: {
      'field.id': {
        handler() {
          this.$nextTick(() => {
            this.$nextTick(this.input);
          });
        },
      },
      'field.value': {
        handler() {
          this.$nextTick(this.resize);
        },
      },
    },
    mounted() {
      if (this.type === 'text') {
        this.resize();
      }

      if (this.autofocus) {
        this.focus();
      }
    },
    methods: {
      mask(value) {
        const {
          format,
          bigFormat,
        } = this.field.mask;

        const greater = value.length > format.length;

        if (this.pattern === bigFormat) {
          if (greater) {
            return;
          }
        } else if (!greater) {
          return;
        }

        this.change();
      },

      focus() {
        const {
          input,
        } = this.$refs;

        if (!input) {
          return;
        }

        if (input.focus) {
          input.focus();
        }
      },
      blur() {
        const {
          input,
        } = this.$refs;

        if (input.blur) {
          input.blur();
        }
      },
      input() {
        const {
          input,
        } = this.$refs;

        if (!input) {
          return;
        }

        this.$emit('input', input.value);

        switch (this.type) {
          case 'text':
            this.resize();
            break;
          case 'patternize':
            this.mask(input.value);
            break;
          default:
            break;
        }
      },
      change() {
        const {
          input,
        } = this.$refs;

        if (!input) {
          return;
        }

        this.$emit('change', input.value);
      },
      resize() {
        const {
          input,
        } = this.$refs;

        input.style.minHeight = 'auto';
        input.style.minHeight = `${input.scrollHeight}px`;

        if (this.height !== input.scrollHeight) {
          this.height = input.scrollHeight;

          this.$emit('resize');
        }
      },
      submit(e) {
        if (this.multiline) {
          return true;
        }

        e.preventDefault();
        this.$emit('submit');

        return false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/_variables.scss';

  .former-input {
    align-items: center;
    display: flex;
    justify-content: center;
    flex-direction: row;
  }

  .former-input-field {
    @include field-reset;
    @include former-field;
    @include former-field-height;

    &::placeholder {
      color: var(--cordel-legacy-color-neutral-primary);
    }

    &:focus {
      color: var(--cordel-legacy-color-constant-dark);
    }

    @include former-field-desktop;
  }

  .former-input-field-focusless {
    &:focus {
      box-shadow: none;
    }
  }

  .former-input-field-center {
    text-align: center;
  }

  .former-input-field-bold {
    font-weight: bold;
    font-size: $font-big;

    &::placeholder {
      color: var(--cordel-legacy-color-neutral-primary);
    }
  }

  .former-input-field-error {
    background-color: var(--cordel-legacy-color-alert-danger);
    color: var(--cordel-legacy-color-constant-light);

    &:focus {
      color: var(--cordel-legacy-color-constant-light);
    }

    &::placeholder {
      color: var(--cordel-legacy-color-constant-light);
    }

    &.former-input-field-empty {
      color: var(--cordel-legacy-color-constant-light);
    }
  }

  .former-input-field-color-white {
    background-color: var(--cordel-legacy-color-constant-light);
  }

  .former-input-field-color-gray {
    border-radius: $button * 0.5;
  }

  .former-input-field-size-big {
    @include former-field-big;
  }

  .former-input-field-multiline {
    height: auto;
    line-height: var(--cordel-legacy-line-height-l);
    resize: none;
  }

  .former-input-field-empty {
    color: var(--cordel-legacy-color-neutral-primary);
  }

  .former-input-field-disabled {
    cursor: not-allowed;
  }
</style>
